import { Link } from 'react-router-dom';
import styles from './Contact.module.css';

const CONTACT_REASONS = [
  {
    title: 'Technical Support',
    description: 'For bugs, errors, or technical issues with experiments or the platform.',
    action: 'Open an issue on GitHub with details about the problem, including browser and steps to reproduce.',
    link: 'https://github.com/staticpayload/axlabs/issues',
  },
  {
    title: 'Feature Requests',
    description: 'For suggestions about new experiments, tools, or platform improvements.',
    action: 'Open an issue on GitHub or check if a similar request already exists.',
    link: 'https://github.com/staticpayload/axlabs/issues',
  },
  {
    title: 'Institutional Partnerships',
    description: 'For schools, universities, or organizations interested in deployment options.',
    action: 'Email us at institutional@axlabs.io with details about your institution and use case.',
    link: 'mailto:institutional@axlabs.io',
  },
  {
    title: 'Research Collaboration',
    description: 'For researchers interested in validation studies or educational research.',
    action: 'Email us at research@axlabs.io with information about your research background and interests.',
    link: 'mailto:research@axlabs.io',
  },
];

const WHAT_TO_INCLUDE = {
  bugs: [
    'Browser and version',
    'Operating system',
    'Steps to reproduce the issue',
    'Expected vs actual behavior',
    'Screenshots if applicable',
  ],
  features: [
    'Specific use case or problem to solve',
    'Why existing solutions are insufficient',
    'Proposed approach or implementation ideas',
    'Willingness to contribute',
  ],
  institutional: [
    'Institution name and type',
    'Intended scale (number of students, courses)',
    'Technical environment (LMS integration, hosting requirements)',
    'Timeline for implementation',
  ],
};

export function Contact() {
  return (
    <div className={styles.contact}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <Link to="/" className={styles.backLink}>← Back to Home</Link>
          <h1 className={styles.title}>Contact</h1>
          <p className={styles.subtitle}>
            Get in touch for technical support, feature requests, or institutional inquiries.
            Before contacting, please review the documentation and existing GitHub issues.
          </p>
        </div>
      </header>

      {/* Contact Channels */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>How to Contact</h2>
          <div className={styles.contactGrid}>
            <div className={styles.contactCard}>
              <h3 className={styles.contactTitle}>GitHub Issues</h3>
              <p className={styles.contactDesc}>
                The primary channel for bug reports and feature requests. Public issues allow
                the community to contribute solutions and benefit from shared knowledge.
              </p>
              <a
                href="https://github.com/staticpayload/axlabs/issues"
                className={styles.contactLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Issues →
              </a>
            </div>
            <div className={styles.contactCard}>
              <h3 className={styles.contactTitle}>Email</h3>
              <p className={styles.contactDesc}>
                For institutional partnerships, research collaboration, or sensitive matters
                that are not appropriate for public issues.
              </p>
              <a href="mailto:contact@axlabs.io" className={styles.contactLink}>
                contact@axlabs.io →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Reasons */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>What to Contact For</h2>
          <div className={styles.reasonsList}>
            {CONTACT_REASONS.map((reason) => (
              <div key={reason.title} className={styles.reasonCard}>
                <h3 className={styles.reasonTitle}>{reason.title}</h3>
                <p className={styles.reasonDesc}>{reason.description}</p>
                <p className={styles.reasonAction}>{reason.action}</p>
                <a
                  href={reason.link}
                  className={styles.reasonLink}
                  target={reason.link.startsWith('mailto') ? undefined : '_blank'}
                  rel={reason.link.startsWith('mailto') ? undefined : 'noopener noreferrer'}
                >
                  {reason.link.startsWith('mailto') ? reason.link.replace('mailto:', '') : 'Open on GitHub'} →
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Include */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>What to Include</h2>
          <div className={styles.includeGrid}>
            <div className={styles.includeCard}>
              <h3 className={styles.includeTitle}>Bug Reports</h3>
              <ul className={styles.includeList}>
                {WHAT_TO_INCLUDE.bugs.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className={styles.includeCard}>
              <h3 className={styles.includeTitle}>Feature Requests</h3>
              <ul className={styles.includeList}>
                {WHAT_TO_INCLUDE.features.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className={styles.includeCard}>
              <h3 className={styles.includeTitle}>Institutional Inquiries</h3>
              <ul className={styles.includeList}>
                {WHAT_TO_INCLUDE.institutional.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Response Time */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Response Expectations</h2>
          <div className={styles.expectationBox}>
            <p className={styles.expectationText}>
              AxLabs is maintained by a small team. We do our best to respond to all inquiries,
              but response times vary. Here are typical expectations:
            </p>
            <ul className={styles.expectationList}>
              <li>
                <strong>GitHub Issues:</strong> Community issues may receive responses from other users.
                Maintainer responses typically occur within 1-2 weeks for triage.
              </li>
              <li>
                <strong>Critical Bugs:</strong> Issues affecting core functionality receive priority.
                Patches are released as needed.
              </li>
              <li>
                <strong>Institutional Inquiries:</strong> Initial response within 1 week.
                Detailed proposals may take longer to evaluate.
              </li>
              <li>
                <strong>Research Collaboration:</strong> Evaluated on a case-by-case basis.
                Include relevant background and timeline expectations.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Before Contacting */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Before Contacting</h2>
          <div className={styles.beforeList}>
            <div className={styles.beforeItem}>
              <span className={styles.beforeNumber}>1</span>
              <div>
                <h3 className={styles.beforeTitle}>Check Documentation</h3>
                <p className={styles.beforeDesc}>
                  The <Link to="/documentation" className={styles.inlineLink}>documentation</Link> covers
                  most common questions about using AxLabs, running experiments, and troubleshooting issues.
                </p>
              </div>
            </div>
            <div className={styles.beforeItem}>
              <span className={styles.beforeNumber}>2</span>
              <div>
                <h3 className={styles.beforeTitle}>Search Existing Issues</h3>
                <p className={styles.beforeDesc}>
                  Check <a href="https://github.com/staticpayload/axlabs/issues" className={styles.inlineLink} target="_blank" rel="noopener noreferrer">GitHub issues</a> to see
                  if your problem or feature request has already been reported. Add your input to existing discussions.
                </p>
              </div>
            </div>
            <div className={styles.beforeItem}>
              <span className={styles.beforeNumber}>3</span>
              <div>
                <h3 className={styles.beforeTitle}>Review Philosophy</h3>
                <p className={styles.beforeDesc}>
                  Some feature requests conflict with AxLabs' <Link to="/philosophy" className={styles.inlineLink}>design philosophy</Link>.
                  We prioritize correctness over convenience, so certain "improvements" won't be implemented.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Don't Do */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>What We Don't Do</h2>
          <div className={styles.dontList}>
            <div className={styles.dontItem}>
              <span className={styles.dontIcon}>×</span>
              <div>
                <h3 className={styles.dontTitle}>No Homework Help</h3>
                <p className={styles.dontDesc}>
                  We don't provide answers to specific homework problems or help with assignments.
                  Contact your instructor or teaching assistant for academic support.
                </p>
              </div>
            </div>
            <div className={styles.dontItem}>
              <span className={styles.dontIcon}>×</span>
              <div>
                <h3 className={styles.dontTitle}>No Custom Development</h3>
                <p className={styles.dontDesc}>
                  We don't build custom experiments or features for individual institutions.
                  Open source contributions are welcome for general-purpose additions.
                </p>
              </div>
            </div>
            <div className={styles.dontItem}>
              <span className={styles.dontIcon}>×</span>
              <div>
                <h3 className={styles.dontTitle}>No Sales Calls</h3>
                <p className={styles.dontDesc}>
                  We don't engage in outbound sales or schedule demo calls.
                  Documentation and GitHub issues provide all necessary information.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <a href="https://github.com/staticpayload/axlabs/issues" className={styles.footerLink} target="_blank" rel="noopener noreferrer">GitHub Issues</a>
          <span className={styles.footerSeparator}>·</span>
          <a href="mailto:contact@axlabs.io" className={styles.footerLink}>Email</a>
          <span className={styles.footerSeparator}>·</span>
          <Link to="/documentation" className={styles.footerLink}>Documentation</Link>
        </div>
      </footer>
    </div>
  );
}
